import { useEffect, useRef, useState } from 'react';
import { CONTEXT_TIMEOUT_MS, TICKET_ACTOR_ID } from '../constants';
import { ContextSubtitleState, TriggerPoint } from '../types';

/**
 * The ticket page's one context read — which does **two jobs**.
 *
 * It assembles the context subtitle *and* it is the existence check of Requirement 8 criterion 15. That
 * is why there is one call and not two, and why the form waits for it: a form rendered while the read is
 * outstanding is a check the user can submit straight past, and a form that appears and then withdraws
 * is worse, since by then the record is written and cannot be unwritten.
 */

/** The wire shape of `POST /api/agentHawk/merlin-feedback/resolve-context`. */
export interface ResolveContextResponse {
  /**
   * Three-valued, not `resolved: boolean`.
   *
   * A boolean cannot distinguish the read *succeeding and finding nothing* from the read *failing* —
   * and that is the one distinction the existence check depends on. `empty` is a proven absence and
   * suppresses the form; `error` is ignorance and must not.
   */
  status: 'events' | 'empty' | 'error';
  /** Present when `status` is `events`. Written to `feedbackDetails`, never into the target reference. */
  eventId?: string;
  /**
   * When the analysis ran. There is deliberately no `domainName` or `symptom`: the analysis blob is
   * `{"kind":"turn","user":...,"assistant":...}` and holds neither, so asking memory for them could only
   * ever return nothing. The page takes `domainName` from the deep link's own path instead.
   */
  analysedAt?: string;
}

export interface ResolveContextRequest {
  triggerPoint: TriggerPoint;
  sessionId: string;
  ticketId?: string;
  domainIdentifier?: string;
  /**
   * The literal `ticket_interceptor` on the ticket Trigger_Point.
   *
   * `ListEvents` is scoped by `(memoryId, actorId, sessionId)`, and a ticket analysis is written under
   * that fixed actor. Reading the right `sessionId` under the *wrong* actor returns an empty list from a
   * **successful** call — which this hook would report as `resolvedEmpty` and the page would turn into
   * `analysis-not-found`, for every ticket in the fleet, while every metric reported success. Never
   * derive this from the authenticated identity.
   */
  actorId?: string;
}

export type ResolveContext = (
  request: ResolveContextRequest
) => Promise<ResolveContextResponse>;

export interface UseFeedbackContext {
  state: ContextSubtitleState;
  /** True only while the read is outstanding. The form is withheld for exactly this interval. */
  isLoading: boolean;
  /**
   * True when the analysis is **provably absent**. The only state that suppresses the form permanently.
   */
  isAnalysisMissing: boolean;
  /** From a `status: 'events'` read. Written to `feedbackDetails` on ticket records. */
  eventId?: string;
}

export function useFeedbackContext(
  triggerPoint: TriggerPoint,
  sessionId: string,
  resolve: ResolveContext,
  options: {
    ticketId?: string;
    domainIdentifier?: string;
    enabled?: boolean;
  } = {}
): UseFeedbackContext {
  const { ticketId, domainIdentifier, enabled = true } = options;
  const [state, setState] = useState<ContextSubtitleState>({
    status: 'loading',
  });
  const settledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    settledRef.current = false;
    /*
     * Back to `loading` before the new read is issued, not only `settledRef` back to false.
     *
     * A dependency change — a different `ticketId`, `sessionId` or Trigger_Point — starts a fresh read, but
     * without this the hook keeps returning the previous read's state while that one is outstanding. So
     * `isLoading` stays false and `eventId` still holds the *previous* analysis run, which defeats the whole
     * reason the form waits on this read: the page would keep the form rendered and a submission could carry
     * an eventId belonging to a different ticket. Attributing feedback to the wrong analysis is worse than
     * withholding the form, and unlike a form that appears and withdraws, it cannot be noticed afterwards.
     *
     * A functional update so a re-run that is already `loading` — the first one — does not force a render for
     * a state it is already in.
     */
    setState((previous) =>
      previous.status === 'loading' ? previous : { status: 'loading' }
    );
    let cancelled = false;

    // The deadline is on the *form*, not merely on the subtitle. On expiry we move to `timedout`, which
    // the page turns into a retryable "unavailable at the moment" outcome. The page is never left in
    // `loading`: an indefinitely-loading page is indistinguishable from one that blocks ratings outright.
    const timer = setTimeout(() => {
      if (cancelled || settledRef.current) return;
      settledRef.current = true;
      setState({ status: 'timedout' });
    }, CONTEXT_TIMEOUT_MS);

    resolve({
      triggerPoint,
      sessionId,
      ticketId,
      domainIdentifier,
      // Fixed actor on the ticket path. See ResolveContextRequest.actorId for why this must not be
      // derived from the authenticated user.
      ...(triggerPoint === 'TICKET' ? { actorId: TICKET_ACTOR_ID } : {}),
    })
      .then((response) => {
        if (cancelled || settledRef.current) return;
        settledRef.current = true;
        clearTimeout(timer);

        if (response.status === 'events') {
          setState({
            status: 'resolved',
            analysedAt: response.analysedAt ?? '',
            eventId: response.eventId ?? '',
          });
          return;
        }
        if (response.status === 'empty') {
          // Succeeded and found nothing: the analysis provably does not exist. This is the one state
          // that suppresses the form, and it must stay distinguishable from `failed` — one is knowing,
          // the other is not knowing.
          setState({ status: 'resolvedEmpty' });
          return;
        }
        setState({ status: 'failed' });
      })
      .catch(() => {
        if (cancelled || settledRef.current) return;
        settledRef.current = true;
        clearTimeout(timer);
        // Existence unknown. The page withholds the form and asks the user to reload, rather than
        // accepting a rating it could not attach to a specific analysis run.
        setState({ status: 'failed' });
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, resolve, sessionId, ticketId, domainIdentifier, triggerPoint]);

  return {
    state,
    isLoading: state.status === 'loading',
    isAnalysisMissing: state.status === 'resolvedEmpty',
    eventId: state.status === 'resolved' ? state.eventId : undefined,
  };
}
